import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreditCard,
  History,
  XCircle,
  AlertCircle,
  ChevronLeft,
  PauseCircle,
  Ban,
  PlayCircle,
} from "lucide-react";
import subscriptionService from "../../../services/subscriptionService";

const SubscriptionManage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [billingPortal, setBillingPortal] = useState(null);
  const [isCanceled, setIsCanceled] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelType, setCancelType] = useState(null); // 'immediate', 'pause' ou 'resume'

  useEffect(() => {
    fetchBillingPortal();
    fetchSubscriptionStatus();
  }, []);

  const fetchBillingPortal = async () => {
    try {
      const response = await subscriptionService.getBillingPortal();
      setBillingPortal(response.billing_portal);
    } catch (error) {
      console.error("Erreur lors du chargement du portail:", error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await subscriptionService.checkSubscription();
      setIsCanceled(response.is_canceled);
    } catch (error) {
      console.error("Erreur lors du chargement du statut:", error);
    }
  };

  const handleSubscriptionAction = async () => {
    try {
      setLoading(true);

      if (cancelType === "immediate") {
        await subscriptionService.cancelNowSubscription();
      } else if (cancelType === "pause") {
        await subscriptionService.pauseSubscription();
      } else if (cancelType === "resume") {
        await subscriptionService.resumeSubscription();
      }

      // Rafraîchir le statut après l'action
      await fetchSubscriptionStatus();

      // Rediriger vers la page d'abonnement
      navigate("/cfp/subscription");
    } catch (error) {
      console.error("Erreur lors de l'opération:", error);
    } finally {
      setLoading(false);
      setShowCancelConfirm(false);
      setCancelType(null);
    }
  };

  const handleActionClick = (type) => {
    setCancelType(type);
    setShowCancelConfirm(true);
  };

  const handleCloseConfirm = () => {
    setShowCancelConfirm(false);
    setCancelType(null);
  };

  // Déterminer le texte et les styles en fonction du type d'action
  const getConfirmDialogContent = () => {
    switch (cancelType) {
      case "immediate":
        return {
          title: "Confirmation d'annulation définitive",
          message:
            "Êtes-vous sûr de vouloir annuler définitivement votre abonnement ? Cette action prendra effet à la fin de votre période de facturation en cours.",
          warning:
            "Vous perdrez l'accès à tous les services associés à votre abonnement.",
          color: "red",
        };
      case "pause":
        return {
          title: "Confirmation de mise en pause",
          message:
            "Êtes-vous sûr de vouloir mettre en pause votre abonnement ?",
          warning:
            "Vous pourrez réactiver votre abonnement à tout moment depuis votre espace. Aucun paiement ne sera effectué pendant la période de pause.",
          color: "orange",
        };
      case "resume":
        return {
          title: "Confirmation de reprise",
          message: "Êtes-vous sûr de vouloir reprendre votre abonnement ?",
          warning:
            "Les paiements reprendront normalement selon les conditions de votre abonnement. Vous aurez à nouveau accès à tous les services.",
          color: "green",
        };
      default:
        return null;
    }
  };

  const confirmContent = getConfirmDialogContent();

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-start gap-4 mb-8">
          <button
            title="Retour"
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            Gérer mon abonnement
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Carte Portail Stripe */}
          <div className="bg-white rounded-lg shadow-sm p-6 border">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold">Facturation</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Gérez vos moyens de paiement, téléchargez vos factures
            </p>
            <button
              onClick={() => window.open(billingPortal, "_blank")}
              className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Accéder au portail Stripe
            </button>
          </div>

          {/* Carte Historique */}
          <div className="bg-white rounded-lg shadow-sm p-6 border">
            <div className="flex items-center gap-3 mb-4">
              <History className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-semibold">Historique</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Consultez l'historique de vos abonnements
            </p>
            <button
              onClick={() => navigate("/cfp/subscription/history")}
              className="w-full bg-gray-600 text-white py-2 rounded-md hover:bg-gray-700 transition-colors"
            >
              Voir l'historique
            </button>
          </div>

          {/* Carte Gestion */}
          <div className="bg-white rounded-lg shadow-sm p-6 border md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <Ban className="w-6 h-6 text-red-600" />
              <h2 className="text-xl font-semibold">
                Gérer la fin de l'abonnement
              </h2>
            </div>

            {!showCancelConfirm ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Option Annulation définitive - Toujours visible mais désactivée si déjà annulé */}
                <div
                  className={`border rounded-lg p-4 transition-colors  hover:border-red-300
                  `}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <XCircle className={`w-5 h-5 text-red-600`} />
                    <h3 className={`font-semibold text-lg `}>
                      Annulation définitive
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    L'abonnement sera définitivement résilié à la fin de la
                    période de facturation en cours. Plus aucun paiement ne sera
                    effectué.
                  </p>
                  <button
                    onClick={() => handleActionClick("immediate")}
                    className={`w-full py-2 rounded-md transition-colors 

                        bg-red-600 text-white hover:bg-red-700
                    `}
                  >
                    Annuler définitivement
                  </button>
                </div>

                {/* Option Mise en pause / Reprise */}
                <div
                  className={`border rounded-lg p-4 transition-colors ${
                    isCanceled
                      ? "hover:border-green-300"
                      : "hover:border-orange-300"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    {isCanceled ? (
                      <PlayCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <PauseCircle className="w-5 h-5 text-orange-600" />
                    )}
                    <h3 className="font-semibold text-lg">
                      {isCanceled
                        ? "Reprendre l'abonnement"
                        : "Mettre en pause"}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    {isCanceled
                      ? "Réactivez votre abonnement pour continuer à profiter de nos services. Les paiements reprendront normalement."
                      : "Mettez votre abonnement en pause. Vous pourrez le réactiver à tout moment. Les paiements seront suspendus temporairement."}
                  </p>
                  <button
                    onClick={() =>
                      handleActionClick(isCanceled ? "resume" : "pause")
                    }
                    className={`w-full py-2 rounded-md transition-colors ${
                      isCanceled
                        ? "bg-green-600 hover:bg-green-700 text-white"
                        : "bg-orange-600 hover:bg-orange-700 text-white"
                    }`}
                  >
                    {isCanceled ? "Reprendre maintenant" : "Mettre en pause"}
                  </button>
                </div>
              </div>
            ) : (
              // Dialogue de confirmation
              <div
                className={`bg-${confirmContent?.color}-50 border border-${confirmContent?.color}-200 rounded-lg p-6`}
              >
                <div
                  className={`flex items-center gap-2 text-${confirmContent?.color}-700 mb-4`}
                >
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-semibold text-lg">
                    {confirmContent?.title}
                  </span>
                </div>

                <p className={`text-${confirmContent?.color}-600 mb-4`}>
                  {confirmContent?.message}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  {confirmContent?.warning}
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={handleSubscriptionAction}
                    disabled={loading}
                    className={`px-6 py-2 rounded-md text-white transition-colors ${
                      cancelType === "immediate"
                        ? "bg-red-600 hover:bg-red-700 disabled:bg-red-300"
                        : cancelType === "pause"
                          ? "bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300"
                          : "bg-green-600 hover:bg-green-700 disabled:bg-green-300"
                    }`}
                  >
                    {loading ? "Traitement..." : "Confirmer"}
                  </button>
                  <button
                    onClick={handleCloseConfirm}
                    className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Information supplémentaire */}
          <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Information importante :</p>
                <p>
                  • En cas d'annulation définitive, vous ne serez plus facturé à
                  partir du prochain cycle.
                  <br />
                  • La mise en pause suspend temporairement votre abonnement.
                  Vous pouvez le réactiver à tout moment.
                  <br />
                  • La reprise d'abonnement réactive immédiatement votre accès
                  et les paiements reprennent normalement.
                  <br />• Pour toute question, contactez notre support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManage;
